import { useState, useEffect, useCallback } from "react";
import { 
  Settings, Package, Calculator, FileText, BarChart3, Lightbulb, 
  CircleDot, Check, X, Loader2, Search, Building2, Save, MonitorPlay
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Switch } from "../../components/ui/switch";
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from "../../components/ui/select";
import { toast } from "sonner";
import { authFetch } from "../../services/authService";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminModules() {
  const [officine, setOfficine] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOfficina, setSelectedOfficina] = useState(null);
  const [moduleConfig, setModuleConfig] = useState(null);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const fetchOfficine = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/admin/users`, { });
      if (res.ok) {
        const users = await res.json();
        // Filtra solo le officine
        const offList = users.filter(u => u.role?.toUpperCase() === "OFFICINA");
        setOfficine(offList);
      }
    } catch (error) {
      console.error("Error fetching officine:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchModuleConfig = useCallback(async (officinaId) => {
    try {
      const res = await authFetch(`${API}/admin/modules/config/${officinaId}`, { });
      if (res.ok) {
        setModuleConfig(await res.json());
      }
    } catch (error) {
      console.error("Error fetching module config:", error);
    }
  }, []);

  useEffect(() => {
    fetchOfficine();
  }, [fetchOfficine]);

  const handleSelectOfficina = (officina) => {
    setSelectedOfficina(officina);
    fetchModuleConfig(officina.user_id);
  };

  const handleToggleModule = (moduleName) => {
    setModuleConfig(prev => ({
      ...prev,
      [moduleName]: !prev[moduleName]
    }));
  };

  const handleSave = async () => {
    if (!selectedOfficina || !moduleConfig) return;
    
    setSaving(true);
    try {
      const res = await authFetch(`${API}/admin/modules/config/${selectedOfficina.user_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          magazzino_enabled: moduleConfig.magazzino_enabled,
          fiscale_enabled: moduleConfig.fiscale_enabled,
          regime_fiscale: moduleConfig.regime_fiscale,
          efattura_enabled: moduleConfig.efattura_enabled,
          statistiche_enabled: moduleConfig.statistiche_enabled,
          ia_suggerimenti_enabled: moduleConfig.ia_suggerimenti_enabled,
          gommista_enabled: moduleConfig.gommista_enabled,
          video_repairs_enabled: moduleConfig.video_repairs_enabled
        })
      });

      if (res.ok) {
        toast.success("Configurazione salvata con successo");
      } else {
        toast.error("Errore nel salvataggio");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    } finally {
      setSaving(false);
    }
  };

  const filteredOfficine = officine.filter(o => 
    o.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    o.business_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    o.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const modules = [
    { 
      key: "magazzino_enabled", 
      name: "Magazzino", 
      icon: Package,
      description: "Gestione articoli, servizi e movimenti di magazzino"
    },
    { 
      key: "fiscale_enabled", 
      name: "Gestione Fiscale", 
      icon: Calculator,
      description: "Regime ordinario/forfettario, calcolo IVA"
    },
    { 
      key: "efattura_enabled", 
      name: "e-Fattura", 
      icon: FileText,
      description: "Generazione XML FatturaPA"
    },
    { 
      key: "statistiche_enabled", 
      name: "Statistiche Avanzate", 
      icon: BarChart3,
      description: "KPI, dashboard e reportistica"
    },
    { 
      key: "ia_suggerimenti_enabled", 
      name: "Suggerimenti IA", 
      icon: Lightbulb,
      description: "Suggerimenti intelligenti basati sui dati"
    },
    { 
      key: "gommista_enabled", 
      name: "Gommista", 
      icon: CircleDot,
      description: "Deposito e cambio pneumatici"
    },
    { 
      key: "video_repairs_enabled", 
      name: "Video Riparazioni", 
      icon: MonitorPlay,
      description: "Libreria video tecnica per diagnosi e riparazioni"
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-modules-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white uppercase font-['Barlow_Condensed'] tracking-wider flex items-center gap-2">
          <Settings className="w-6 h-6 text-primary" />
          Gestione Moduli
        </h1>
        <p className="text-zinc-400 text-sm">Attiva/disattiva moduli per ogni officina</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Lista Officine */}
        <div className="card p-4 bg-zinc-900/50 border border-zinc-800">
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input
                type="text"
                placeholder="Cerca officina..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-zinc-800 border-zinc-700"
              />
            </div>
          </div>
          
          <div className="space-y-2 max-h-[500px] overflow-y-auto">
            {filteredOfficine.map((off) => (
              <div
                key={off.user_id}
                onClick={() => handleSelectOfficina(off)}
                className={`p-3 rounded-lg cursor-pointer transition-all ${
                  selectedOfficina?.user_id === off.user_id
                    ? "bg-primary/20 border border-primary"
                    : "bg-zinc-800/50 border border-transparent hover:border-zinc-700"
                }`}
              >
                <div className="flex items-center gap-3">
                  <Building2 className={`w-5 h-5 ${
                    selectedOfficina?.user_id === off.user_id ? "text-primary" : "text-zinc-500"
                  }`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-semibold truncate">
                      {off.business_name || off.name}
                    </p>
                    <p className="text-zinc-500 text-xs truncate">{off.email}</p>
                  </div>
                </div>
              </div>
            ))}
            
            {filteredOfficine.length === 0 && (
              <p className="text-zinc-500 text-center py-8">Nessuna officina trovata</p>
            )}
          </div>
        </div>

        {/* Configurazione Moduli */}
        <div className="lg:col-span-2">
          {selectedOfficina && moduleConfig ? (
            <div className="card p-6 bg-zinc-900/50 border border-zinc-800">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-lg font-bold text-white uppercase font-['Barlow_Condensed']">
                    {selectedOfficina.business_name || selectedOfficina.name}
                  </h2>
                  <p className="text-zinc-400 text-sm">{selectedOfficina.email}</p>
                </div>
                <Button onClick={handleSave} disabled={saving}>
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Salva
                </Button>
              </div>

              <div className="space-y-4">
                {modules.map((mod) => {
                  const Icon = mod.icon;
                  const isEnabled = moduleConfig[mod.key];
                  
                  return (
                    <div 
                      key={mod.key}
                      className={`p-4 rounded-lg border transition-all ${
                        isEnabled 
                          ? "bg-primary/10 border-primary/30" 
                          : "bg-zinc-800/30 border-zinc-700"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`p-2 rounded-lg ${isEnabled ? "bg-primary/20" : "bg-zinc-700"}`}>
                            <Icon className={`w-5 h-5 ${isEnabled ? "text-primary" : "text-zinc-400"}`} />
                          </div>
                          <div>
                            <h3 className="text-white font-semibold">{mod.name}</h3>
                            <p className="text-zinc-400 text-sm">{mod.description}</p>
                          </div>
                        </div>
                        <Switch
                          checked={isEnabled}
                          onCheckedChange={() => handleToggleModule(mod.key)}
                        />
                      </div>
                      
                      {/* Opzioni aggiuntive per Fiscale */}
                      {mod.key === "fiscale_enabled" && isEnabled && (
                        <div className="mt-4 pl-12">
                          <label className="text-sm text-zinc-400 mb-2 block">Regime Fiscale</label>
                          <Select 
                            value={moduleConfig.regime_fiscale || "ordinario"}
                            onValueChange={(v) => setModuleConfig({...moduleConfig, regime_fiscale: v})}
                          >
                            <SelectTrigger className="w-48 bg-zinc-800 border-zinc-700">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="ordinario">Ordinario</SelectItem>
                              <SelectItem value="forfettario">Forfettario</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Riepilogo */}
              <div className="mt-6 p-4 bg-zinc-800/50 rounded-lg">
                <h4 className="text-white font-semibold mb-2">Moduli Attivi</h4>
                <div className="flex flex-wrap gap-2">
                  {modules.filter(m => moduleConfig[m.key]).map((mod) => (
                    <span 
                      key={mod.key}
                      className="px-3 py-1 bg-primary/20 text-primary rounded-full text-sm flex items-center gap-1"
                    >
                      <Check className="w-3 h-3" />
                      {mod.name}
                    </span>
                  ))}
                  {modules.filter(m => moduleConfig[m.key]).length === 0 && (
                    <span className="text-zinc-500 text-sm">Nessun modulo attivo</span>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="card p-12 bg-zinc-900/50 border border-zinc-800 flex items-center justify-center">
              <div className="text-center">
                <Settings className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
                <h3 className="text-white font-semibold mb-2">Seleziona un'officina</h3>
                <p className="text-zinc-400 text-sm">
                  Seleziona un'officina dalla lista per gestire i moduli
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
